/* global femcare, axios, google */

femcare.storeLocator = {
    map: null,
    markers: [],
    locations: [],
    currentActiveIndex: null,

    /**
     * Initialize the store locator component.
     */
    async init() {
        this.storeLocator = document.querySelector('.store-locator');

        if (this.storeLocator) {
            this.searchInput = document.querySelector('.store-locator__search input');
            this.suggestions = document.querySelector('.store-locator__suggestions');
            this.suggestionsBackdrop = document.querySelector('.store-locator__suggestions-backdrop');
            this.suggestionsStores = document.querySelector('.store-locator__suggestions-stores');

            this.infoCount = document.querySelector('.store-locator__info-count');
            this.mapContainer = document.getElementById('store-locator-map');
            this.cardList = document.querySelector('.store-locator__list');

            this.labelShow = this.storeLocator.getAttribute('data-label-show') || 'Show on Map';
            this.labelPhone = this.storeLocator.getAttribute('data-label-phone') || 'Phone number:';
            this.labelDirections = this.storeLocator.getAttribute('data-label-directions') || 'Get Directions';
            this.labelEmptySearch = this.storeLocator.getAttribute('data-label-empty-search') || 'No stores found for this location.';

            await this.fetchLocations();
            this.initMap();
            this.addMarkers();
            this.renderLocationList();
            this.bindEvents();
            this.bindMobileNavigation();

            if (window.innerWidth < 768) {
                this.switchToMapView();
                setTimeout(() => this.fitMapToAllLocations(), 100);
            }
        }
    },

    /**
     * Fetch locations from the backend using Axios.
     */
    async fetchLocations() {
        try {
            const antiforgeryToken = await femcare.form.protection.provideCSRFToken();
            const storeLocatorApiUrl = femcare.shared.util.resolveProperApi(
                'ajax/core/storelocator/getall',
                '/json/mock-api/store-locator.json'
            );

            const response = await axios.get(storeLocatorApiUrl, {
                headers: {
                    'X-Request-Verification-Token': antiforgeryToken,
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const stores = Array.isArray(response.data)
                ? response.data
                : response.data.extra.storeLocations;
            this.locations = stores.map((loc) => ({
                ...loc,
                latitude: parseFloat(loc.latitude),
                longitude: parseFloat(loc.longitude),
                directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${parseFloat(loc.latitude)},${parseFloat(loc.longitude)}`,
            }));
        } catch (error) {
            this.handleMapError();
        }
    },

    /**
     * Initialize the map using Google Maps API.
     */
    initMap() {
        if (!this.locations.length) {
            this.handleMapError();
            return;
        }

        this.map = new google.maps.Map(this.mapContainer, {
            zoom: 2,
            center: { lat: 0, lng: 0 }
        });

        this.fitMapToAllLocations();
    },

    /**
     * Zoom the map out to fit all store pins.
     */
    fitMapToAllLocations() {
        const bounds = new google.maps.LatLngBounds();
        this.locations.forEach((loc) => {
            if (!isNaN(loc.latitude) && !isNaN(loc.longitude)) {
                bounds.extend({ lat: loc.latitude, lng: loc.longitude });
            }
        });

        if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds);
            const maxZoom = 12;
            if (this.map.getZoom() > maxZoom) {
                this.map.setZoom(maxZoom);
            }
        } else {
            this.handleMapError();
        }
    },

    /**
     * Add markers and clusters to the map.
     */
    addMarkers() {
        const rawMarkerIconSvg = `
            <svg width="23" height="31" viewBox="0 0 23 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.88799 30.0899C10.6764 31.0775 12.1722 31.0775 12.9606 30.0899C17.1279 24.8694 22.8486 17.0053 22.8486 11.4243C22.8486 5.11483 17.7338 0 11.4243 0C5.11483 0 0 5.11483 0 11.4243C0 17.0053 5.72065 24.8694 9.88799 30.0899ZM11.4243 16.451C14.2005 16.451 16.451 14.2005 16.451 11.4243C16.451 8.64818 14.2005 6.39766 11.4243 6.39766C8.64818 6.39766 6.39766 8.64818 6.39766 11.4243C6.39766 14.2005 8.64818 16.451 11.4243 16.451Z" fill="url(#paint0_linear_9077_15637)"/>
                <defs>
                    <linearGradient id="paint0_linear_9077_15637" x1="0" y1="15.4153" x2="22.8486" y2="15.4153" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#D11A88"/>
                        <stop offset="0.494792" stop-color="#DA3482"/>
                        <stop offset="1" stop-color="#DA291C"/>
                    </linearGradient>
                </defs>
            </svg>
        `;

        const rawSelectedMarkerIconSvg = `
            <svg width="23" height="31" viewBox="0 0 23 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.88799 30.0899C10.6764 31.0775 12.1722 31.0775 12.9606 30.0899C17.1279 24.8694 22.8486 17.0053 22.8486 11.4243C22.8486 5.11483 17.7338 0 11.4243 0C5.11483 0 0 5.11483 0 11.4243C0 17.0053 5.72065 24.8694 9.88799 30.0899ZM11.4243 16.451C14.2005 16.451 16.451 14.2005 16.451 11.4243C16.451 8.64818 14.2005 6.39766 11.4243 6.39766C8.64818 6.39766 6.39766 8.64818 6.39766 11.4243C6.39766 14.2005 8.64818 16.451 11.4243 16.451Z" fill="#001E62"/>
            </svg>
        `;

        const rawClusterIconSvg = `
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="16" fill="url(#paint0_linear_9225_4756)"/>
                <defs>
                    <linearGradient id="paint0_linear_9225_4756" x1="0" y1="16" x2="32" y2="16" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#D11A88"/>
                        <stop offset="0.494792" stop-color="#DA3482"/>
                        <stop offset="1" stop-color="#DA291C"/>
                    </linearGradient>
                </defs>
            </svg>
        `;

        const encodedMarkerIconSvg = encodeURIComponent(rawMarkerIconSvg).replace(/%20/g, ' ');
        const encodedSelectedMarkerIconSvg = encodeURIComponent(rawSelectedMarkerIconSvg).replace(/%20/g, ' ');
        const encodedClusterIconSvg = encodeURIComponent(rawClusterIconSvg).replace(/%20/g, ' ');

        this.defaultMarkerIcon = `data:image/svg+xml;charset=UTF-8,${encodedMarkerIconSvg}`;
        this.selectedMarkerIcon = `data:image/svg+xml;charset=UTF-8,${encodedSelectedMarkerIconSvg}`;

        // Create markers for each location.
        const markers = this.locations.map((loc, index) => {
            if (isNaN(loc.latitude) || isNaN(loc.longitude)) {
                return null;
            }

            const marker = new google.maps.Marker({
                position: { lat: loc.latitude, lng: loc.longitude },
                icon: this.defaultMarkerIcon
            });

            marker.addListener('click', () => this.selectStore(index));
            return marker;
        }).filter(Boolean);

        // Save the markers so we can update their icons later.
        this.markers = markers;

        // Create the marker cluster.
        /* global markerClusterer */
        this.markerCluster = new markerClusterer.MarkerClusterer({
            markers,
            map: this.map,
            maxZoom: 14,
            gridSize: 60,
            renderer: {
                render({ count, position }) {
                    const label = {
                        text: String(count),
                        color: 'white',
                        fontSize: '14px',
                    };
                    const icon = {
                        url: `data:image/svg+xml;charset=UTF-8,${encodedClusterIconSvg}`,
                        scaledSize: new google.maps.Size(32, 32),
                    };
                    return new google.maps.Marker({
                        position,
                        label,
                        icon,
                    });
                }
            }
        });
    },

    /**
     * Create the list of store cards.
     */
    renderLocationList() {
        this.cardList.innerHTML = '';

        this.locations.forEach((loc, index) => {
            const card = this.createCard(loc, index);
            this.cardList.appendChild(card);
        });

        this.updateVisibleLocations();
    },

    /**
     * Create a single card element.
     */
    createCard(location, index) {
        const card = document.createElement('li');
        card.className = 'store-locator__item';
        card.setAttribute('data-index', index);

        card.innerHTML = `
            <h3 class="store-locator__item-title">${location.name}</h3>
            <p class="store-locator__item-address">${location.address}, ${location.zipCode}</p>
            <p class="store-locator__item-address">${location.city}, ${location.state}</p>
            <p class="store-locator__item-phone">
                ${this.labelPhone}
                <a href="tel:${location.phoneNumber}">${location.phoneNumber}</a>
            </p>
            <div class="store-locator__item-actions">
                <button class="btn btn-primary store-locator__item-button" data-index="${index}">
                    ${this.labelShow}
                </button>
                <a class="btn store-locator__item-button store-locator__item-button--directions"
                    data-index="${index}"
                    href="${location.directionsUrl}"
                    target="_blank">
                    ${this.labelDirections}
                    <i class="icon-external-link"></i>
                </a>
            </div>
        `;

        // "Show on map" button: switch to map view on mobile, then select the store.
        card.querySelector('.store-locator__item-button').addEventListener('click', () => {
            if (window.innerWidth < 768) {
                this.switchToMapView();
                setTimeout(() => this.selectStore(index), 50);
            } else {
                this.selectStore(index);
            }
        });

        return card;
    },

    /**
     * Select a store and handle view-dependent behavior:
     * - Desktop: highlight card + scroll to it in list + center map.
     * - Mobile list view: highlight card + scroll to it in list.
     * - Mobile map view: highlight card + center map + show mobile card at bottom.
     */
    selectStore(index) {
        const location = this.locations[index];
        const cards = this.cardList.querySelectorAll('.store-locator__item');
        const activeCard = this.cardList.querySelector(`[data-index="${index}"]`);

        // Update marker icons.
        if (this.currentActiveIndex !== null && this.currentActiveIndex !== index) {
            const prevMarker = this.markers[this.currentActiveIndex];
            if (prevMarker) {
                prevMarker.setIcon(this.defaultMarkerIcon);
            }
        }
        if (this.markers[index]) {
            this.markers[index].setIcon(this.selectedMarkerIcon);
        }
        this.currentActiveIndex = index;

        // Highlight the active card in the list.
        cards.forEach((card, i) => {
            card.classList.toggle('store-locator__item--active', i === index);
        });

        const isMobile = window.innerWidth < 768;
        const isMapView = this.mapContainer.classList.contains('displayed');

        if (!isMobile) {
            // Desktop: center map and scroll card into view in the list panel.
            this.centerMapOnStore(location);
            this.scrollCardIntoView(activeCard);
        } else if (isMapView) {
            // Mobile map view: center map and show mini card below the map.
            this.centerMapOnStore(location);
            this.showMobileCard(activeCard);
        } else {
            // Mobile list view: scroll the card into view.
            if (activeCard) {
                activeCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }
    },

    /**
     * Center the map on a location and zoom in.
     */
    centerMapOnStore(location) {
        this.map.setCenter({ lat: location.latitude, lng: location.longitude });
        this.map.setZoom(12);
    },

    /**
     * Scroll the active card into the center of the list panel (desktop).
     */
    scrollCardIntoView(activeCard) {
        const listWrapper = document.querySelector('.store-locator__list-wrapper');
        if (!listWrapper || !activeCard) return;

        const containerRect = listWrapper.getBoundingClientRect();
        const cardRect = activeCard.getBoundingClientRect();
        const currentScroll = listWrapper.scrollTop;
        const offset = (cardRect.top - containerRect.top) - (containerRect.height / 2) + (cardRect.height / 2);
        listWrapper.scrollTo({ top: currentScroll + offset, behavior: 'smooth' });
    },

    /**
     * Clone the active card into the mobile card container below the map and scroll it into view.
     */
    showMobileCard(activeCard) {
        const mapCardContainer = document.querySelector('.store-locator__active-card');
        if (!mapCardContainer || !activeCard) return;

        mapCardContainer.innerHTML = `
            <div class="${activeCard.className}" data-index="${activeCard.getAttribute('data-index')}">
                ${activeCard.innerHTML}
            </div>
        `;
        mapCardContainer.classList.remove('hidden');

        setTimeout(() => {
            const newActiveCard = mapCardContainer.firstElementChild;
            if (newActiveCard) {
                newActiveCard.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }
        }, 50);
    },

    /**
     * Update the visible locations count based on currently shown cards.
     */
    updateVisibleLocations() {
        const visibleCount = this.cardList.querySelectorAll('.store-locator__item:not(.hidden)').length;
        this.infoCount.textContent = visibleCount;
    },

    /**
     * Dynamically filter and render suggestions based on the search query.
     * Searches by city, state, or postal code.
     * Each suggestion shows the store name and address.
     */
    handleSearch() {
        const query = this.searchInput.value.toLowerCase().trim();

        if (query === '') {
            this.suggestions.classList.add('hidden');
            this.suggestionsBackdrop.classList.add('hidden');
            return;
        }

        const matches = this.locations
            .map((loc, index) => ({ loc, index }))
            .filter(({ loc }) =>
                loc.city.toLowerCase().includes(query) ||
                loc.state.toLowerCase().includes(query) ||
                loc.zipCode.toLowerCase().includes(query)
            );

        this.suggestionsStores.innerHTML = '';

        if (matches.length === 0) {
            const noResults = document.createElement('li');
            noResults.className = 'store-locator__suggestion store-locator__suggestion--empty';
            noResults.textContent = this.labelEmptySearch;
            this.suggestionsStores.appendChild(noResults);
        } else {
            matches.forEach(({ loc, index }) => {
                const li = document.createElement('li');
                li.className = 'store-locator__suggestion';
                li.setAttribute('data-index', index);
                li.innerHTML = `
                    <span class="store-locator__suggestion-name">${loc.name}</span>
                    <span class="store-locator__suggestion-address">${loc.address}, ${loc.zipCode}, ${loc.city}, ${loc.state}</span>
                `;
                li.addEventListener('click', () => {
                    this.suggestions.classList.add('hidden');
                    this.suggestionsBackdrop.classList.add('hidden');
                    this.selectStore(index);
                });
                this.suggestionsStores.appendChild(li);
            });
        }

        this.suggestions.classList.remove('hidden');
        this.suggestionsBackdrop.classList.remove('hidden');
    },

    /**
     * Switch the mobile view to map tab.
     */
    switchToMapView() {
        this.mapContainer.classList.add('displayed');
        this.cardList.classList.add('hidden');

        const listBtnItem = document.querySelector('.store-locator-nav__button--list')?.parentElement;
        const mapBtnItem = document.querySelector('.store-locator-nav__button--map')?.parentElement;
        if (listBtnItem && mapBtnItem) {
            mapBtnItem.classList.add('store-locator-nav__item--active');
            listBtnItem.classList.remove('store-locator-nav__item--active');
        }

        if (this.map) {
            setTimeout(() => google.maps.event.trigger(this.map, 'resize'), 50);
        }
    },

    /**
     * Switch the mobile view to list tab.
     */
    switchToListView() {
        this.mapContainer.classList.remove('displayed');
        this.cardList.classList.remove('hidden');

        const listBtnItem = document.querySelector('.store-locator-nav__button--list')?.parentElement;
        const mapBtnItem = document.querySelector('.store-locator-nav__button--map')?.parentElement;
        if (listBtnItem && mapBtnItem) {
            listBtnItem.classList.add('store-locator-nav__item--active');
            mapBtnItem.classList.remove('store-locator-nav__item--active');
        }
    },

    /**
     * Handle mobile navigation between List view and Map view.
     */
    bindMobileNavigation() {
        const listBtn = document.querySelector('.store-locator-nav__button--list');
        const mapBtn = document.querySelector('.store-locator-nav__button--map');

        listBtn.addEventListener('click', () => {
            this.switchToListView();

            if (this.currentActiveIndex !== null) {
                setTimeout(() => {
                    const activeCard = this.cardList.querySelector(`[data-index="${this.currentActiveIndex}"]`);
                    if (activeCard) {
                        activeCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }
                }, 50);
            }
        });

        mapBtn.addEventListener('click', () => {
            this.switchToMapView();

            if (this.currentActiveIndex !== null) {
                // A store is selected: re-center map on it and show the mobile card.
                setTimeout(() => {
                    const location = this.locations[this.currentActiveIndex];
                    this.centerMapOnStore(location);

                    const activeCard = this.cardList.querySelector(`[data-index="${this.currentActiveIndex}"]`);
                    this.showMobileCard(activeCard);
                }, 50);
            } else {
                // No store selected: zoom out to show all pins and hide the mobile card.
                const mapCardContainer = document.querySelector('.store-locator__active-card');
                if (mapCardContainer) {
                    mapCardContainer.classList.add('hidden');
                    mapCardContainer.innerHTML = '';
                }
                this.fitMapToAllLocations();
            }
        });
    },

    /**
     * Bind events for search input and outside clicks.
     */
    bindEvents() {
        if (this.searchInput) {
            const debouncedSearch = femcare.shared.util.debounce(() => this.handleSearch(), 300);
            this.searchInput.addEventListener('input', debouncedSearch);
        }

        document.addEventListener('click', (event) => {
            if (!this.searchInput || !this.suggestions) {
                return;
            }
            const clickedInsideInput = this.searchInput.contains(event.target);
            const clickedInsideSuggestions = this.suggestions.contains(event.target);
            if (!clickedInsideInput && !clickedInsideSuggestions) {
                this.suggestions.classList.add('hidden');
                this.suggestionsBackdrop.classList.add('hidden');
            }
        });
    },

    /**
     * Handle map errors (e.g. no valid locations).
     */
    handleMapError() {
        const mapErrorText = this.mapContainer.getAttribute('data-map-error') || 'An error occurred.';
        this.mapContainer.classList.add('store-locator__map--error');
        this.mapContainer.innerHTML = `
            <i class="icon-validation-error"></i>
            <p>${mapErrorText}</p>
        `;
    },
};
